import React, { useCallback, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { X } from "lucide-react";
import { ASSISTANT_RESULT, MAX_ASSISTANT_MESSAGE_CHARS } from "../../lib/assistantClient";

/**
 * Phase 4A.5A — the Assistant drawer. Presentation only.
 *
 * It owns no conversation, no request and no state that outlives a keystroke:
 * `AssistantProvider` holds the turns in React memory and passes them down, so
 * there is exactly one place where Assistant state can exist and exactly one
 * place that can clear it. The provider portals this into `document.body` and
 * makes the application root inert while it is open.
 *
 * **Assistant output is text.** Every turn is rendered as a React child, which
 * React escapes. There is no `dangerouslySetInnerHTML`, no Markdown renderer,
 * no link autodetection and no HTML parsing anywhere in this file — model
 * output is untrusted input, and the only safe thing to do with it is show it.
 *
 * **Nothing focusable is ever disabled.** A turn in flight makes the textarea
 * read-only and marks the send button `aria-disabled`, because `disabled` on
 * the focused element hands focus to the document body — outside the dialog,
 * where neither Escape nor the Tab cycle can reach it any more.
 */

/** Focusable descendants, in DOM order, for the Tab cycle below. */
const FOCUSABLE =
  'button:not([disabled]), textarea:not([disabled]), input:not([disabled]), a[href], [tabindex]:not([tabindex="-1"])';

const INPUT_ID = "qw-assistant-input";
const COUNTER_ID = "qw-assistant-counter";
const SESSION_NOTE_ID = "qw-assistant-session-note";
const ERROR_ID = "qw-assistant-input-error";

/** The Bulgarian sentence for a non-answer state — ours, never the server's. */
function stateKey(result) {
  return `assistant.state_${result}`;
}

export default function AssistantPanel({
  turns,
  draft,
  pending,
  inputError,
  onDraftChange,
  onSubmit,
  onReset,
  onClose,
}) {
  const { t } = useTranslation();
  const panelRef = useRef(null);
  const inputRef = useRef(null);
  const logRef = useRef(null);

  // Opening the panel puts the caret where the user is going to type; the
  // provider restores focus to the current launcher when this unmounts.
  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  // Keep the newest turn visible without moving page focus.
  useEffect(() => {
    const log = logRef.current;
    if (log) log.scrollTop = log.scrollHeight;
  }, [turns, pending]);

  const onKeyDown = useCallback(
    (event) => {
      if (event.key === "Escape") {
        event.stopPropagation();
        onClose();
        return;
      }
      if (event.key !== "Tab") return;
      const nodes = panelRef.current?.querySelectorAll(FOCUSABLE);
      if (!nodes || nodes.length === 0) return;
      const first = nodes[0];
      const last = nodes[nodes.length - 1];
      // A dialog that lets Tab escape into the page behind it is a dialog a
      // keyboard user cannot close.
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    },
    [onClose],
  );

  const onInputKeyDown = useCallback(
    (event) => {
      // Enter sends, Shift+Enter makes a new line — the convention every user
      // of a message box already has. The provider's in-flight guard is what
      // keeps a second turn off the wire, so this needs no check of its own.
      if (event.key === "Enter" && !event.shiftKey) {
        event.preventDefault();
        onSubmit();
      }
    },
    [onSubmit],
  );

  const used = Array.from(draft).length;
  const overLimit = used > MAX_ASSISTANT_MESSAGE_CHARS;
  const invalid = overLimit || Boolean(inputError);
  const describedBy = [COUNTER_ID, SESSION_NOTE_ID, inputError ? ERROR_ID : null]
    .filter(Boolean)
    .join(" ");

  return (
    <>
      <div
        className="qw-assistant-backdrop"
        onClick={onClose}
        data-testid="assistant-backdrop"
        aria-hidden="true"
      />
      <div
        className="qw-assistant-panel"
        role="dialog"
        aria-modal="true"
        aria-labelledby="qw-assistant-title"
        data-testid="assistant-panel"
        ref={panelRef}
        onKeyDown={onKeyDown}
      >
        <div className="qw-assistant-head">
          <h2 className="qw-assistant-title" id="qw-assistant-title">
            {t("assistant.title")}
          </h2>
          <button
            type="button"
            className="qw-btn sm ghost"
            onClick={onReset}
            data-testid="assistant-reset"
          >
            {t("assistant.reset")}
          </button>
          <button
            type="button"
            className="qw-assistant-close"
            onClick={onClose}
            aria-label={t("assistant.close")}
            data-testid="assistant-close"
          >
            <X size={16} strokeWidth={2} aria-hidden="true" />
          </button>
        </div>

        {/* The capability notice is deliberately narrow: the Assistant answers
            questions and does nothing else. Claiming more would be a claim the
            backend cannot honour — it exposes no write tool at all. */}
        <p className="qw-assistant-notice" data-testid="assistant-notice">
          {t("assistant.notice")}
        </p>

        <div
          className="qw-assistant-log"
          role="log"
          aria-live="polite"
          aria-label={t("assistant.conversation")}
          data-testid="assistant-conversation"
          ref={logRef}
        >
          {turns.length === 0 && !pending && (
            <p className="qw-assistant-empty" data-testid="assistant-empty">
              {t("assistant.empty")}
            </p>
          )}
          {turns.map((turn) => (
            <div
              key={turn.id}
              className={`qw-assistant-turn ${turn.author}${
                turn.result && turn.result !== ASSISTANT_RESULT.ANSWER ? " state" : ""
              }`}
              data-testid={`assistant-turn-${turn.author}`}
              data-result={turn.result || undefined}
            >
              <span className="qw-assistant-author">
                {turn.author === "user"
                  ? t("assistant.author_user")
                  : t("assistant.author_assistant")}
              </span>
              {/* Plain text child. React escapes it; CSS preserves newlines. */}
              <span className="qw-assistant-text">
                {turn.author === "user" || turn.result === ASSISTANT_RESULT.ANSWER
                  ? turn.text
                  : // A state with no copy of its own still fails closed to the
                    // generic sentence rather than rendering a bare key.
                    t(stateKey(turn.result), {
                      defaultValue: t("assistant.state_assistant_unavailable"),
                    })}
              </span>
            </div>
          ))}
          {pending && (
            <div className="qw-assistant-turn assistant state" data-testid="assistant-loading">
              <span className="qw-assistant-author">{t("assistant.author_assistant")}</span>
              <span className="qw-assistant-text">{t("assistant.loading")}</span>
            </div>
          )}
        </div>

        <form
          className="qw-assistant-form"
          data-testid="assistant-form"
          onSubmit={(event) => {
            event.preventDefault();
            onSubmit();
          }}
        >
          <label className="qw-visually-hidden" htmlFor={INPUT_ID}>
            {t("assistant.input_label")}
          </label>
          {/* No `maxLength`: the browser would silently TRUNCATE a pasted
              question, and a question cut short is a different question. Over
              the limit is refused here and, authoritatively, by the backend.
              `readOnly` rather than `disabled` while pending — see the file
              comment: disabling the focused element empties the dialog. */}
          <textarea
            id={INPUT_ID}
            className="qw-textarea qw-assistant-input"
            data-testid="assistant-input"
            rows={3}
            value={draft}
            placeholder={t("assistant.placeholder")}
            readOnly={pending}
            aria-disabled={pending || undefined}
            aria-describedby={describedBy}
            aria-invalid={invalid || undefined}
            aria-errormessage={inputError ? ERROR_ID : undefined}
            onChange={(event) => onDraftChange(event.target.value)}
            onKeyDown={onInputKeyDown}
            ref={inputRef}
          />
          <div className="qw-assistant-foot">
            <span
              id={COUNTER_ID}
              className={`qw-assistant-counter${overLimit ? " over" : ""}`}
              data-testid="assistant-counter"
            >
              {t("assistant.counter", { used, max: MAX_ASSISTANT_MESSAGE_CHARS })}
            </span>
            <button
              type="submit"
              className="qw-btn primary sm"
              aria-disabled={pending || undefined}
              data-testid="assistant-send"
            >
              {pending ? t("assistant.sending") : t("assistant.send")}
            </button>
          </div>
          {inputError && (
            <p
              id={ERROR_ID}
              className="qw-error"
              role="alert"
              data-testid="assistant-input-error"
            >
              {inputError}
            </p>
          )}
          <p
            id={SESSION_NOTE_ID}
            className="qw-assistant-session"
            data-testid="assistant-session-note"
          >
            {t("assistant.session_note")}
          </p>
        </form>
      </div>
    </>
  );
}
